/**
 * SpotSchedulePrintView Component
 *
 * Print-optimized list view of aircraft parking schedule.
 * Displays spot-by-spot schedule in table format for operational use.
 * Hidden on screen, only visible when printing.
 */

import { Box, Typography } from "@mui/material";
import { addHours, format } from "date-fns";
import React, { useMemo } from "react";
import { Shape } from "../../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import {
  ParkingRequest,
  Placement,
  Spot,
} from "../../TemporalTest/types";

interface SpotSchedulePrintViewProps {
  spots: Spot[];
  aircraft: ParkingRequest[];
  placement: Placement;
  horizonHours: number;
  startTime: Date;
  rampShapes?: Shape[];
  statistics?: {
    totalAircraft: number;
    placedAircraft: number;
    unplacedAircraft: number;
    averageMoves?: number;
  };
  rampName?: string;
  dateRange?: string;
}

interface SpotAssignment {
  tailNumber: string;
  aircraftType: string;
  arrival: string;
  departure: string;
  duration: string;
  notes: string;
}

/**
 * Calculate dwell duration in human-readable format
 */
function calculateDwell(startBucket: number, endBucket: number): string {
  const totalMinutes = (endBucket - startBucket) * 15;
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Format bucket to readable date/time in 24-hour format
 */
function formatBucketTime(bucket: number, baseDate: Date): string {
  const date = addHours(baseDate, bucket * 0.25); // 15-minute buckets
  return format(date, "MMM d, HH:mm");
}

export const SpotSchedulePrintView: React.FC<SpotSchedulePrintViewProps> = ({
  spots,
  aircraft,
  placement,
  horizonHours,
  startTime,
  rampShapes = [],
  statistics,
  rampName = "Ramp",
  dateRange,
}) => {
  // Use start time as base date for timeline calculations
  const baseDate = startTime;

  // Create spot ID to name mapping
  const spotIdToName = useMemo(() => {
    const mapping: Record<string, string> = {};
    rampShapes.forEach((shape) => {
      if (shape.type === "Spot" && shape.text) {
        mapping[shape.id] = shape.text;
      }
    });
    return mapping;
  }, [rampShapes]);

  // Create aircraft map for quick lookup
  const aircraftMap = useMemo(() => {
    const map: Record<string, ParkingRequest> = {};
    aircraft.forEach((ac) => {
      map[ac.id] = ac;
    });
    return map;
  }, [aircraft]);

  // Build spot assignments
  const spotAssignments = useMemo(() => {
    const assignments: Record<string, SpotAssignment[]> = {};

    // Initialize all spots
    spots.forEach((spot) => {
      assignments[spot.id] = [];
    });

    // Populate assignments from placement
    aircraft.forEach((ac) => {
      const placed = placement[ac.id];
      if (!placed) return;

      const aircraftType = ac.aircraft?.make && ac.aircraft?.model
        ? `${ac.aircraft.make} ${ac.aircraft.model}`
        : "Unknown Type";

      if ("segments" in placed) {
        // Segmented placement - add each segment
        placed.segments.forEach((segment, idx) => {
          const assignment: SpotAssignment = {
            tailNumber: ac.tail_number,
            aircraftType,
            arrival: formatBucketTime(segment.startBucket, baseDate),
            departure: formatBucketTime(segment.endBucket, baseDate),
            duration: calculateDwell(segment.startBucket, segment.endBucket),
            notes: `Segment ${idx + 1}/${placed.segments.length} (${placed.moveCount} moves)`,
          };

          if (!assignments[segment.spotId]) {
            assignments[segment.spotId] = [];
          }
          assignments[segment.spotId].push(assignment);
        });
      } else {
        // Single placement
        const assignment: SpotAssignment = {
          tailNumber: ac.tail_number,
          aircraftType,
          arrival: formatBucketTime(ac.arrivalBucket, baseDate),
          departure: formatBucketTime(ac.departureBucket, baseDate),
          duration: calculateDwell(ac.arrivalBucket, ac.departureBucket),
          notes: "",
        };

        if (!assignments[placed.spotId]) {
          assignments[placed.spotId] = [];
        }
        assignments[placed.spotId].push(assignment);
      }
    });

    // Sort assignments within each spot by arrival time
    Object.keys(assignments).forEach((spotId) => {
      assignments[spotId].sort((a, b) => a.arrival.localeCompare(b.arrival));
    });

    return assignments;
  }, [spots, aircraft, placement, baseDate]);

  // Deduplicate and sort spots by name
  const sortedSpots = useMemo(() => {
    // First deduplicate by ID
    const uniqueById = new Map<string, Spot>();
    spots.forEach((spot) => {
      uniqueById.set(spot.id, spot);
    });

    // Then deduplicate by display name (in case same name, different IDs)
    const uniqueByName = new Map<string, Spot>();
    Array.from(uniqueById.values()).forEach((spot) => {
      const spotName = spotIdToName[spot.id] || spot.id;
      // Keep first occurrence of each name
      if (!uniqueByName.has(spotName)) {
        uniqueByName.set(spotName, spot);
      }
    });

    // Convert to array and sort by name
    return Array.from(uniqueByName.values()).sort((a, b) => {
      const nameA = spotIdToName[a.id] || a.id;
      const nameB = spotIdToName[b.id] || b.id;
      return nameA.localeCompare(nameB, undefined, { numeric: true });
    });
  }, [spots, spotIdToName]);

  // Calculate date range if not provided
  const displayDateRange = dateRange ||
    `${format(baseDate, "MMM d, yyyy")} - ${format(addHours(baseDate, horizonHours), "MMM d, yyyy")}`;

  return (
    <Box className="print-view-container">
      {/* Header */}
      <Box className="print-view-header">
        <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
          AutoPark Results - {rampName} - {displayDateRange}
        </Typography>

        {/* Statistics */}
        {statistics && (
          <Box className="print-view-stats">
            <Box className="print-view-stat-box">
              <Typography variant="caption" display="block">
                Aircraft Placed
              </Typography>
              <Typography variant="h6" component="div">
                {statistics.placedAircraft} / {statistics.totalAircraft}
              </Typography>
            </Box>

            <Box className="print-view-stat-box">
              <Typography variant="caption" display="block">
                Success Rate
              </Typography>
              <Typography variant="h6" component="div">
                {Math.round((statistics.placedAircraft / statistics.totalAircraft) * 100)}%
              </Typography>
            </Box>

            {statistics.averageMoves !== undefined && statistics.averageMoves > 0 && (
              <Box className="print-view-stat-box">
                <Typography variant="caption" display="block">
                  Average Moves
                </Typography>
                <Typography variant="h6" component="div">
                  {statistics.averageMoves.toFixed(1)}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Spot Sections */}
      {sortedSpots.map((spot) => {
        const spotName = spotIdToName[spot.id] || spot.id;
        const assignments = spotAssignments[spot.id] || [];

        return (
          <Box key={spot.id} className="print-view-spot-section">
            {/* Spot Header */}
            <Box className="print-view-spot-header">
              {spotName}
            </Box>

            {/* Assignments Table */}
            {assignments.length > 0 ? (
              <table className="print-view-table">
                <thead>
                  <tr>
                    <th>Tail Number</th>
                    <th>Aircraft Type</th>
                    <th>Arrival</th>
                    <th>Departure</th>
                    <th>Duration</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assignment, idx) => (
                    <tr key={idx}>
                      <td><strong>{assignment.tailNumber}</strong></td>
                      <td>{assignment.aircraftType}</td>
                      <td>{assignment.arrival}</td>
                      <td>{assignment.departure}</td>
                      <td>{assignment.duration}</td>
                      <td>{assignment.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Box className="print-view-empty">
                (No aircraft scheduled)
              </Box>
            )}
          </Box>
        );
      })}

      {/* Unplaced Aircraft */}
      {statistics && statistics.unplacedAircraft > 0 && (
        <Box className="gantt-unplaced" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Unplaced Aircraft ({statistics.unplacedAircraft})
          </Typography>
          <Typography variant="body2">
            {aircraft
              .filter((ac) => !placement[ac.id])
              .map((ac) => ac.tail_number)
              .join(", ")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
